/**
 * expandEditable — universal inline-editability pre-pass.
 *
 * The builder's edit runtime discovers editable fields by walking the DOM for
 * `data-builder-field` markers. Historically those markers (and the editor
 * type) were authored in two places that had to stay in sync: a `data-builder-field`
 * attribute in the rjBuild, AND a separate `fields[]` manifest on the block def.
 *
 * The universal format collapses this to a single, self-describing source: a
 * render node declares its own editability inline via an `editable:` key, e.g.
 *
 *   - type: h2
 *     editable: { field: title, type: text, label: 'Titre' }
 *     content: ~~.title
 *
 * reactive-json only forwards a node's `attributes:` to the DOM — an unknown
 * top-level key like `editable:` is silently dropped (see lib `Html.jsx`).
 * `expandEditable` therefore runs once, at normalize time, and rewrites every
 * `editable:` declaration into the `data-*` attributes the runtime reads:
 *
 *   data-builder-field   = editable.field
 *   data-builder-type    = editable.type ?? editable.editorType
 *   data-builder-options = JSON of the remaining editor options (only if any)
 *   class               += ' builder-editable-field'   (for the hover outline)
 *
 * A declaration carrying a `column:` (a cell of a list row, inside a Switch
 * template) is instead rewritten into a `BuilderRowField` primitive — see
 * `toRowField` below.
 *
 * Second, symmetrical pass — the TEXTE INDICATIF (placeholder) of a form field.
 * An `<input>` has no text node to turn contentEditable, and its placeholder is
 * already declared: `inputAttributes: { placeholder: ~~.….placeholderEmail }`.
 * That declaration IS the editability statement (it names a field of the unit),
 * so nothing has to be re-authored — `placeholderFieldOf` reads the path against
 * the enclosing unit's `dataBaseLocation` and stamps the resolved key as
 * `data-builder-placeholder-field` on the control. The runtime
 * (`BuilderBlockWrapper`) then edits THAT field, in the control itself, instead
 * of letting an editor fill the form in. A placeholder that is a literal, or a
 * path pointing anywhere else than a top-level field of the unit, is left alone:
 * the control keeps the live behaviour it has today (facet, search preview).
 *
 * Troisième passe, du même esprit — les LIAISONS AU CONTENU d'une page-modèle.
 * Un nœud qui affiche `~~._context_.<clé>` déclare, par ce seul chemin, qu'il
 * montre un champ de l'objet servi à cette URL. Le chemin est estampillé en
 * `data-builder-context-field` ; il ne rend rien éditable par lui-même (en
 * rendu public comme en édition de PAGE l'attribut est inerte) — c'est
 * l'éditeur d'objet, qui seul connaît le type courant et son mapping, qui
 * décide au clic si ce point tombe sur un champ réel (`resolveContextField`).
 *
 * Quatrième passe, corollaire de la troisième — LE MASQUAGE D'UN CHAMP VIDE.
 * Un bloc de modèle masque ce que le contenu ne porte pas (`what: hide` +
 * `when: ~~._context_.X` + `isEmpty: true`) : chez le visiteur, une catégorie
 * absente ne doit pas laisser une pastille vide. En ÉDITION, cette règle
 * retirait de l'écran tout ce qui reste à renseigner — rien à voir, rien à
 * cliquer, et un contenu neuf s'ouvrait sur une page quasi blanche où seul le
 * titre, jamais masqué, offrait son filigrane. Chaque masquage de ce genre est
 * donc conditionné EN PLUS à l'absence du drapeau `_contextEdit_`
 * (`CONTEXT_EDIT_PATH`), que seul l'éditeur d'un contenu dans son modèle pose
 * dans le store : le rendu public masque comme avant, l'éditeur voit le champ
 * vide, avec son filigrane. Rien à déclarer dans les YAML — c'est la condition
 * elle-même qui dit « cet élément montre ce champ », et elle sert AUSSI à
 * estampiller la liaison quand le nœud n'en porte aucune (le cas d'un
 * conteneur dont l'enfant, `TermLabel`, ne rend aucune balise).
 *
 * Design contract:
 *  - PURE & immutable: never mutates its input; returns new objects. This keeps
 *    it idempotent and safe to call on shared registry data.
 *  - NON-DESTRUCTIVE: a node without `editable:` is returned deep-equal. A build
 *    with zero `editable:` keys round-trips unchanged (golden-master guarantee).
 *  - NON-CLOBBERING: an existing hand-authored `data-builder-field` is never
 *    overwritten, so legacy blocks keep working alongside the inline format.
 *  - IDEMPOTENT: the `editable:` key is consumed (removed) on expansion, so a
 *    second pass is a no-op.
 */

import { pickEditorOptions } from './editorOptions'
import { CONTEXT_EDIT_PATH, contextPathOf } from './contextFields'

const EDITABLE_CLASS = 'builder-editable-field'

const isPlainObject = (v) =>
  v != null && typeof v === 'object' && !Array.isArray(v)

/** Append `builder-editable-field` to a className string without duplicating. */
const withEditableClass = (className) => {
  const base = typeof className === 'string' ? className : ''
  const classes = base.split(/\s+/).filter(Boolean)
  if (classes.includes(EDITABLE_CLASS)) return base || EDITABLE_CLASS
  classes.push(EDITABLE_CLASS)
  return classes.join(' ')
}

/**
 * Build the `attributes` object for a node carrying `editable:`, merging onto
 * any attributes the node already declares. Never clobbers an existing
 * `data-builder-field`.
 */
const applyEditable = (attributes, editable) => {
  const attrs = isPlainObject(attributes) ? { ...attributes } : {}

  if (editable.field != null && attrs['data-builder-field'] == null) {
    attrs['data-builder-field'] = editable.field
  }

  const type = editable.type ?? editable.editorType
  if (type != null && attrs['data-builder-type'] == null) {
    attrs['data-builder-type'] = type
  }

  const options = pickEditorOptions(editable)
  if (Object.keys(options).length > 0 && attrs['data-builder-options'] == null) {
    attrs['data-builder-options'] = JSON.stringify(options)
  }
  if (options.placeholder != null && attrs['data-placeholder'] == null) {
    attrs['data-placeholder'] = options.placeholder
  }

  attrs.class = withEditableClass(attrs.class)
  return attrs
}

/**
 * A cell of a list row — `editable: { field, column, type }` — can't be
 * expanded to static attributes: the row index only exists at render time.
 * The node is rewritten into a `BuilderRowField` primitive (its original
 * `type` becomes the rendered `tag`), which derives the row path from the
 * store and emits the same `data-builder-*` markers with the row baked in.
 *
 * One carrier isn't an HTML tag: `BuilderHtmlField`, the rich-text renderer.
 * A rich-text COLUMN is authored on it exactly like a whole rich-text field,
 * and is rewritten into a `div` cell flagged `html: true` — `BuilderRowField`
 * then injects the value as HTML instead of rendering it as children. Without
 * this the cell would render the tag name `BuilderHtmlField` as an unknown
 * element and print the markup as escaped text.
 */
const HTML_CARRIERS = { BuilderHtmlField: 'div' }

const toRowField = (node, editable) => {
  const { editable: _consumed, type, content, ...rest } = node
  const htmlTag = HTML_CARRIERS[type]
  const out = {
    ...rest,
    type: 'BuilderRowField',
    tag: htmlTag ?? type ?? 'span',
    field: editable.field,
    column: editable.column,
    editorType: editable.type ?? editable.editorType,
    options: pickEditorOptions(editable),
  }
  if (htmlTag) out.html = true
  if (content !== undefined) out.content = content
  return out
}

/**
 * The keys under which reactive-json nests a CHILD rjBuild fragment.
 *
 * `content` is the universal one. `label` is the form-field slot: the library's
 * `Input` / `TextAreaField` / `SelectField` / `CheckBoxField` render `props.label`
 * through `<View>` (not as a plain string), so a field's `<label>` can host real
 * nodes — and therefore an `editable:` declaration. That is what makes a form's
 * *intitulés* editable IN PLACE (inline UX rule) instead of through a row of
 * `builder-edit-only` pills parked next to the form.
 *
 * Anything else (a primitive's scalar `label`, `options[].label`, …) is left
 * alone: `expandNode` is a no-op on scalars, so listing a slot only ever costs
 * a walk.
 */
const FRAGMENT_SLOTS = ['content', 'label']

/**
 * Where a subtree's data lives. A `BuilderUnit` declares it
 * (`dataBaseLocation: ~~.apiData.data.objectData.data`); outside any unit the
 * store root `~~` is the base. It is the SAME base a field key is resolved
 * against when the runtime commits (`commitField(fieldKey, …)`) — hence only a
 * path of the form `<base>.<key>` (ONE segment, no dot) yields a field key.
 */
const DEFAULT_DATA_BASE = '~~'

/** The unit field a form control's placeholder is bound to, or null. */
const placeholderFieldOf = (node, base) => {
  const declared = isPlainObject(node.inputAttributes)
    ? node.inputAttributes.placeholder ?? node.placeholder
    : node.placeholder
  if (typeof declared !== 'string') return null
  const prefix = base.endsWith('.') ? base : `${base}.`
  if (!declared.startsWith(prefix)) return null
  const key = declared.slice(prefix.length)
  return /^[A-Za-z0-9_-]+$/.test(key) ? key : null
}

/** Stamp the resolved key on the CONTROL's own attributes (not the wrapper's). */
const applyPlaceholderField = (node, key) => {
  const attrs = isPlainObject(node.inputAttributes) ? { ...node.inputAttributes } : {}
  if (attrs['data-builder-placeholder-field'] != null) return node
  attrs['data-builder-placeholder-field'] = key
  return { ...node, inputAttributes: attrs }
}

/**
 * Le chemin d'objet qu'un nœud AFFICHE. `content:` couvre le texte rendu,
 * `value:` les primitives qui reçoivent la valeur entière (`LazyImage`).
 * Un chemin RELATIF (`~.name`, dans un `Switch`) n'en est pas un : la ligne
 * n'existe qu'au rendu — c'est le domaine des cellules (`BuilderRowField`).
 */
const contextFieldOf = (node) => contextPathOf(node.content) ?? contextPathOf(node.value)

/** Stamp the context path on the node's own attributes. */
const applyContextField = (node, path) => {
  const attrs = isPlainObject(node.attributes) ? { ...node.attributes } : {}
  if (attrs['data-builder-context-field'] != null) return node
  attrs['data-builder-context-field'] = path
  return { ...node, attributes: attrs }
}

/** Les deux clés sous lesquelles une condition reactive-json en groupe d'autres. */
const CONDITION_GROUPS = ['andConditions', 'orConditions']

/** `what: hide` — la casse est libre côté lib (`hide` comme `Hide`). */
const isHideAction = (action) =>
  isPlainObject(action) && typeof action.what === 'string' && action.what.toLowerCase() === 'hide'

/**
 * Le premier chemin d'objet qu'un arbre de conditions interroge, ou null.
 * Descend dans les groupes : `andConditions: [{when: ~~._context_.cover}, …]`.
 */
const contextPathInCondition = (condition) => {
  if (!isPlainObject(condition)) return null
  const direct = contextPathOf(condition.when)
  if (direct) return direct
  for (const group of CONDITION_GROUPS) {
    if (!Array.isArray(condition[group])) continue
    for (const sub of condition[group]) {
      const found = contextPathInCondition(sub)
      if (found) return found
    }
  }
  return null
}

/** Le garde-fou est-il DÉJÀ posé ? (la passe doit rester idempotente) */
const hasContextEditGuard = (condition) => {
  if (!isPlainObject(condition)) return false
  if (condition.when === CONTEXT_EDIT_PATH) return true
  return CONDITION_GROUPS.some(
    (group) => Array.isArray(condition[group]) && condition[group].some(hasContextEditGuard),
  )
}

/**
 * Conditionne EN PLUS chaque masquage lié au contenu à l'absence du drapeau
 * d'édition d'objet. Les clés de condition vivent à PLAT sur l'action (la lib
 * évalue l'action elle-même comme condition) : elles sont donc déplacées telles
 * quelles dans un `andConditions`, à côté du garde-fou.
 *
 * Hors édition d'objet, `~~._contextEdit_` est absent — donc vide, donc le
 * garde-fou passe et le masquage s'applique exactement comme avant.
 */
const guardHideActions = (node) => {
  if (!Array.isArray(node.actions)) return node
  let changed = false
  const actions = node.actions.map((action) => {
    // Un masquage RÉACTIF (`on:`) répond à un événement, pas à l'état du
    // contenu : il ne relève pas de cette règle.
    if (!isHideAction(action) || action.on !== undefined) return action
    if (hasContextEditGuard(action) || !contextPathInCondition(action)) return action
    changed = true
    const { what, ...condition } = action
    return { what, andConditions: [condition, { when: CONTEXT_EDIT_PATH, isEmpty: true }] }
  })
  return changed ? { ...node, actions } : node
}

/**
 * Le chemin d'objet qu'un nœud masque quand il est vide — une déclaration
 * d'affichage aussi explicite qu'un `content:`, et parfois la SEULE : un
 * conteneur dont l'unique enfant est un `TermLabel` (qui ne rend aucune balise)
 * n'a rien d'autre pour dire qu'il montre la catégorie.
 */
const hideContextPathOf = (node) =>
  (Array.isArray(node.actions)
    ? node.actions.reduce(
      (found, action) => found ?? (isHideAction(action) ? contextPathInCondition(action) : null),
      null,
    )
    : null)

/**
 * Un descendant porte-t-il déjà une liaison ? Le conteneur ne réclame alors
 * PAS celle de sa condition de masquage : deux éléments emboîtés liés au même
 * champ donneraient deux filets, deux filigranes et deux cibles de clic.
 */
const hasContextDescendant = (content) => {
  if (Array.isArray(content)) return content.some(hasContextDescendant)
  if (!isPlainObject(content)) return false
  if (isPlainObject(content.attributes) && content.attributes['data-builder-context-field'] != null) return true
  return FRAGMENT_SLOTS.some((slot) => slot in content && hasContextDescendant(content[slot]))
}

/**
 * Recursively transform one rjBuild node. Children live under the fragment
 * slots (array of nodes, a single node, or a scalar/template string). Other
 * keys (attributes, value, name, style, template paths…) are copied as-is.
 */
const expandNode = (node, base = DEFAULT_DATA_BASE) => {
  // Scalars and template strings ('~~.title', ' Background', numbers) pass through.
  if (!isPlainObject(node)) return node

  let out = node

  if (isPlainObject(node.editable)) {
    if (node.editable.column != null && node.editable.field != null) {
      out = toRowField(node, node.editable)
    } else {
      const { editable, ...rest } = node
      out = { ...rest, attributes: applyEditable(node.attributes, editable) }
    }
  }

  // A unit re-bases everything under it (its own `dataBaseLocation`).
  const childBase = typeof node.dataBaseLocation === 'string' && node.dataBaseLocation
    ? node.dataBaseLocation
    : base

  const placeholderField = placeholderFieldOf(out, childBase)
  if (placeholderField) out = applyPlaceholderField(out, placeholderField)

  // Les enfants D'ABORD : c'est leur expansion qui dit si l'un d'eux porte
  // déjà la liaison que la condition de masquage de ce nœud désigne.
  for (const slot of FRAGMENT_SLOTS) {
    if (slot in out) out = { ...out, [slot]: expandContent(out[slot], childBase) }
  }

  let contextPath = contextFieldOf(out)
  if (!contextPath && !FRAGMENT_SLOTS.some((slot) => hasContextDescendant(out[slot]))) {
    contextPath = hideContextPathOf(out)
  }
  if (contextPath) out = applyContextField(out, contextPath)

  return guardHideActions(out)
}

/** Transform a node's `content`, which may be an array, a node, or a scalar. */
const expandContent = (content, base = DEFAULT_DATA_BASE) => {
  if (Array.isArray(content)) return content.map((child) => expandNode(child, base))
  if (isPlainObject(content)) return expandNode(content, base)
  return content
}

/**
 * Expand a full rjBuild. Walks `renderView` (array of nodes) and every entry of
 * `templates` (a map of name → node). `data` and any other top-level keys are
 * preserved untouched.
 *
 * @param {object|null|undefined} build
 * @returns {object|null|undefined} a new build with `editable:` expanded
 */
export const expandEditable = (build) => {
  if (!isPlainObject(build)) return build

  const out = { ...build }

  if (Array.isArray(build.renderView)) {
    out.renderView = build.renderView.map((node) => expandNode(node))
  }

  if (isPlainObject(build.templates)) {
    const templates = {}
    for (const [name, tpl] of Object.entries(build.templates)) {
      templates[name] = expandNode(tpl)
    }
    out.templates = templates
  }

  return out
}

export default expandEditable
